/**
 * @typedef {'campaign'|'adSet'} Level
 */

/**
 * If called on an ad set, set the level to 'adSet' and provide both campaign and adSet
 * If called on a campaign, set the level to 'campaign' and provide just campaign
 * @param {object} args
 * @param {Level} args.level
 * @param {object} args.campaign
 * @param {object} [args.adSet]
 */
function _whatIsEditable({ level, campaign, adSet = {} }) {
  let isCampaign = level == 'campaign' || level == 'campaigns';
  let isAdSet = level == 'adSet' || level == 'adSets';
  let channels = campaign.channels || campaign.channelCampaigns[0].channels;
  let isFacebook = (channels && channels.includes('facebook')) || campaign.channels.includes('instagram');
  let isGoogle = channels && channels.includes('google');

  let isBudgetCampaignLevel =
    Boolean(campaign.channelCampaigns[0].budget) && campaign.channelCampaigns[0].budget.amount != null;
  let isBudgetAdSetLevel = !isBudgetCampaignLevel;

  let schedule = isCampaign ? campaign.schedule : adSet.schedule;
  let isScheduleContinuously = Boolean(schedule && (schedule.isContinuously || schedule.continuously));

  let doesStartLater = new Date(schedule.startDate) > new Date();
  let doesStartTomorrowOrLater = new Date(schedule.startDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
  let doesStartTodayOrLater = new Date(schedule.startDate).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
  let doesEndInThePast = new Date(schedule.endDate) < new Date();
  let doesEndToday = new Date(schedule.endDate).setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0);

  let isBudgetAmountEditable =
    (isGoogle && isCampaign) ||
    (isFacebook && isCampaign && isBudgetCampaignLevel) ||
    (isFacebook && isAdSet && isBudgetAdSetLevel);
  let isBudgetVisible = isBudgetAmountEditable;
  let isBudgetTypeVisible = isFacebook && ((isCampaign && isBudgetCampaignLevel) || (isAdSet && isBudgetAdSetLevel));
  let isBudgetTypeEditable = false;

  let isScheduleVisible = (isGoogle && isCampaign) || (isFacebook && isAdSet);
  // TODO: The start date should also be editable if the campaign has never been launched.
  let isScheduleStartDateEditable = doesStartTomorrowOrLater;
  let isScheduleStartTimeVisible = isScheduleVisible && isFacebook;
  let isScheduleStartTimeEditable = isScheduleStartTimeVisible && doesStartTodayOrLater && doesStartLater;
  let isScheduleEndDateVisible = !isScheduleContinuously;
  let isScheduleEndDateEditable = isScheduleEndDateVisible;
  let isScheduleEndTimeVisible = isFacebook && isScheduleEndDateVisible;
  let isScheduleEndTimeEditable = isScheduleEndTimeVisible;
  let isScheduleEndDateValid = { value: true };
  let isScheduleEndTimeValid = { value: true };
  if (doesEndInThePast && !doesEndToday && isScheduleEndDateEditable)
    isScheduleEndDateValid = { value: false, reason: 'ENDS_IN_PAST' };
  if (doesEndInThePast && doesEndToday && isScheduleEndTimeEditable)
    isScheduleEndTimeValid = { value: false, reason: 'ENDS_IN_PAST' };

  return {
    budget: {
      visible: isBudgetVisible,
      amount: {
        visible: isBudgetVisible,
        editable: isBudgetAmountEditable,
      },
      type: {
        visible: isBudgetTypeVisible,
        editable: isBudgetTypeEditable,
      },
    },
    schedule: {
      visible: isScheduleVisible,
      startDate: {
        visible: isScheduleVisible,
        editable: isScheduleStartDateEditable,
      },
      startTime: {
        visible: isScheduleStartTimeVisible,
        editable: isScheduleStartTimeEditable,
      },
      endType: {
        visible: true,
        editable: true,
      },
      endDate: {
        visible: isScheduleEndDateVisible,
        editable: isScheduleEndDateEditable,
        valid: isScheduleEndDateValid,
      },
      endTime: {
        visible: isScheduleEndTimeVisible,
        editable: isScheduleEndTimeEditable,
        valid: isScheduleEndTimeValid,
      },
    },
  };
}

/**
 * Decides which options are visible and editable in a campaign.
 * @param {object} campaign campaign.
 */
function whatIsEditableInCampaign(campaign) {
  return _whatIsEditable({ level: 'campaign', campaign, adSet: {} });
}

/**
 * Decides which options are visible and editable in an adset.
 * @param {object} campaign parent campaign of the adset.
 * @param {object} adSet adset.
 */
function whatIsEditableInAdSet(campaign, adSet) {
  return _whatIsEditable({ level: 'adSet', campaign, adSet });
}

module.exports = { whatIsEditableInCampaign, whatIsEditableInAdSet };
